import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { GridColDef, GridEventListener, GridRowParams, useGridApiRef, ValueOptions } from '@mui/x-data-grid-premium';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import JoyRide, { ACTIONS, CallBackProps, Placement, STATUS } from 'react-joyride';
import { AppDispatch } from '../middleware/thunks';
import { I18nKeys } from '../constants/I18nKeys';
import { GroupMember } from '../types/GroupMember';
import { OrderOwner } from '../types/OrderOwner';
import {
  getApplyQuickFilterFnVersionedEmailId,
  getOrdersCreatedFormatter,
  getValueOrEmpty,
} from '../utils/MUIDataGridUtils';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';
import { formatPrice } from '../utils/pricingUtils';
import {
  getOrderDealerFromOrder,
  getOrderOwnerFromOrder,
  getOrderStatusFromOrder,
  getOrderStatusLabelFromOrder,
} from '../utils/orderUtils';
import { OrderOwnerSelector } from './OrderOwnerSelector';
import { hyphenateString } from '../utils/stringUtils';
import { getSiteFromClientId } from '../utils/clientIdUtils';
import { useAppDispatch, useAppSelector } from '../hooks';
import { unknownGroup } from '../constants/Group';
import { AppState } from '../types/AppState';
import { MuiDataGrid } from './MUIDataGrid';
import { setSearchHidden } from '../ducks/search';
import { isCurrentUserAdmin, isCurrentUserManager, isIdeaRoomUser } from '../utils/userUtils';
import { setSecondaryActions } from '../ducks/secondaryActions';
import { useOrderLeadRepo } from '../hooks/useOrderLeadRepo';
import { Configurator, ConfiguratorEnabledOnProps } from '../types/Configurator';
import { OrderDealerSelector } from './OrderDealerSelector';
import { openLeadDetailDialog, setLeads, setOrderDealer, setOrderOwner, setOrderStatus } from '../ducks/leadOrderSlice';
import { Dealer } from '../types/Dealer';
import { JoyrideTooltip } from './JoyrideTooltip';
import { setRunLeadsTour } from '../ducks/gettingStartedSlice';
import { NewWindowLink } from './NewWindowLink';
import { orderStatusOptions, OrderStatusSelector } from './OrderStatusSelector';
import { OrderStatus } from '../types/OrderStatus';
import { anyHasSmartBuildIntegrationEnabled, getEnabledOnProperty } from '../utils/vendorDataUtils';
import { i18n } from '../i18n';
import { getSmartBuildLink } from '../utils/smartbuildUtils';

const getColumnExtraProps = (
  t: Function,
  dispatch: AppDispatch,
  members?: GroupMember[],
  configurators: Configurator[] = [],
  orderOwnerOptions: OrderOwner[] = [],
  orderDealerOptions: Dealer[] = [],
  getOrderStatusOptions?: () => ValueOptions[],
  getBuildingStyleOptions?: () => ValueOptions[],
  getBuildingSizeOptions?: () => ValueOptions[],
  dealersEnabled = false,
  isAdmin = false,
  isManager = false,
): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t(I18nKeys.TableHeaderCreated),
      headerClassName: I18nKeys.TableHeaderCreated,
      valueFormatter: getOrdersCreatedFormatter,
      type: 'dateTime',
      minWidth: 88,
      filterable: false,
    },
    {
      field: 'customerName',
      headerName: t(I18nKeys.TableHeaderName),
      headerClassName: I18nKeys.TableHeaderName,
      type: 'string',
      minWidth: 144,
    },
    {
      field: 'customerEmail',
      headerName: t(I18nKeys.TableHeaderEmail),
      headerClassName: I18nKeys.TableHeaderEmail,
      type: 'string',
      minWidth: 176,
    },
    {
      field: 'customerPhone',
      headerName: t(I18nKeys.TableHeaderPhone),
      headerClassName: I18nKeys.TableHeaderPhone,
      valueFormatter: formatPhoneNumber,
      type: 'string',
      minWidth: 120,
    },
    {
      field: 'city',
      headerName: t(I18nKeys.TableHeaderCity),
      headerClassName: I18nKeys.TableHeaderCity,
      type: 'string',
      valueGetter: (_, row) => getValueOrEmpty(row.city),
      minWidth: 88,
    },
    {
      field: 'state',
      headerName: t(I18nKeys.TableHeaderState),
      headerClassName: I18nKeys.TableHeaderState,
      type: 'string',
      valueGetter: (_, row) => getValueOrEmpty(row.state),
      minWidth: 80,
    },
    {
      field: 'postalCode',
      headerName: t(I18nKeys.TableHeaderDeliveryZip),
      headerClassName: I18nKeys.TableHeaderDeliveryZip,
      type: 'string',
      valueGetter: (_, row) => getValueOrEmpty(row.zipCode),
      minWidth: 78,
    },
    {
      field: 'buildingStyle',
      headerName: t(I18nKeys.TableHeaderBuildingStyle),
      headerClassName: I18nKeys.TableHeaderBuildingStyle,
      type: 'singleSelect',
      minWidth: 126,
      valueOptions: getBuildingStyleOptions?.() || [],
    },
    {
      field: 'buildingSize',
      headerName: t(I18nKeys.TableHeaderBuildingSize),
      headerClassName: I18nKeys.TableHeaderBuildingSize,
      type: 'singleSelect',
      valueOptions: getBuildingSizeOptions?.() || [],
    },
    {
      field: 'totalPrice',
      headerName: t(I18nKeys.TableHeaderEstimate),
      headerClassName: I18nKeys.TableHeaderEstimate,
      valueFormatter: (value, row) => formatPrice(value, row?.transaction?.currency || 'USD', 2),
      type: 'number',
      minWidth: 88,
    },
    {
      field: 'versionedEmailId',
      headerName: t(I18nKeys.OrderGridHeaderOrderNumber),
      headerClassName: I18nKeys.OrderGridHeaderOrderNumber,
      type: 'string',
      minWidth: 160,
      valueFormatter: (value) => hyphenateString(value),
      getApplyQuickFilterFn: getApplyQuickFilterFnVersionedEmailId,
    },
    {
      field: 'clientId',
      headerName: t(I18nKeys.TableHeaderSite),
      headerClassName: I18nKeys.TableHeaderSite,
      type: 'string',
      minWidth: 120,
      valueGetter: (value) => getSiteFromClientId(value, configurators),
    },
  ];

  if (dealersEnabled && (isAdmin || orderDealerOptions.length > 1)) {
    columns.push({
      field: 'Dealer',
      headerName: t(I18nKeys.TableHeaderDealer),
      headerClassName: I18nKeys.TableHeaderDealer,
      type: 'singleSelect',
      minWidth: 152,
      valueOptions: orderDealerOptions.map((dealer) => ({
        value: dealer.id,
        label: dealer.name,
      })),
      valueGetter: (_, row) => getOrderDealerFromOrder(row).id,
      renderCell: (params) => {
        if (isAdmin || isManager) {
          return (
            <Box>
              <OrderDealerSelector
                readonly={false}
                dealer={getOrderDealerFromOrder(params.row)}
                options={orderDealerOptions.filter((o) => o.clientId === params.row.clientId)}
                onOrderDealerChange={(orderDealer: Dealer): void => {
                  dispatch(
                    setOrderDealer({
                      clientId: params.row.clientId,
                      uuid: params.row.uuid,
                      orderStatus: getOrderStatusLabelFromOrder(params.row),
                      orderDealer,
                    }),
                  );
                }}
              />
            </Box>
          );
        }
        return <span>{getOrderDealerFromOrder(params.row).name}</span>;
      },
    });
  }

  if (isAdmin || isManager) {
    columns.push({
      field: 'Owner',
      headerName: t(I18nKeys.TableHeaderOwner),
      headerClassName: I18nKeys.TableHeaderOwner,
      type: 'singleSelect',
      minWidth: 152,
      valueOptions: orderOwnerOptions.map((owner) => ({
        value: owner.email || owner.name,
        label: owner.name || owner.email,
      })),
      valueGetter: (_, row) => getOrderOwnerFromOrder(row, members).email || getOrderOwnerFromOrder(row, members).name,
      renderCell: (params) => (
        <Box>
          <OrderOwnerSelector
            readonly={false}
            owner={getOrderOwnerFromOrder(params.row, members)}
            options={orderOwnerOptions}
            onOrderOwnerChange={(orderOwner: OrderOwner): void => {
              dispatch(
                setOrderOwner({
                  clientId: params.row.clientId,
                  uuid: params.row.uuid,
                  orderStatus: getOrderStatusLabelFromOrder(params.row),
                  orderOwner,
                }),
              );
            }}
          />
        </Box>
      ),
    });
  }

  columns.push(
    {
      field: 'Status',
      headerName: t(I18nKeys.TableHeaderStatus),
      headerClassName: I18nKeys.TableHeaderStatus,
      type: 'singleSelect',
      minWidth: 152,
      valueOptions: () => getOrderStatusOptions?.() || [],
      valueGetter: (_, row) => getOrderStatusFromOrder(row).id,
      renderCell: (params) => (
        <Box>
          <OrderStatusSelector
            readonly={false}
            status={getOrderStatusFromOrder(params.row)}
            onOrderStatusChange={(orderStatus: OrderStatus): void => {
              dispatch(setOrderStatus({ clientId: params.row.clientId, uuid: params.row.uuid, orderStatus }));
            }}
          />
        </Box>
      ),
    },
    {
      field: 'design',
      headerName: t(I18nKeys.OrderGridHeaderDesign),
      headerClassName: I18nKeys.OrderGridHeaderDesign,
      valueGetter: (value, row) => row?.orderDealerLink || row?.link,
      renderCell: (params) => <NewWindowLink link={params.value} />,
      filterable: false,
    },
  );

  if (anyHasSmartBuildIntegrationEnabled(configurators)) {
    columns.push({
      field: 'smartbuildJobId',
      headerName: t(I18nKeys.OrderGridHeaderSmartBuild),
      headerClassName: I18nKeys.OrderGridHeaderSmartBuild,
      valueGetter: (value, row) => getSmartBuildLink(row?.smartbuildJobId),
      renderCell: (params) => <NewWindowLink link={params.value} />,
      filterable: false,
    });
  }

  return columns;
};

export const Leads: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const leads = useAppSelector((state: AppState) => state.leadOrders.leads);
  const user = useAppSelector((state: AppState) => state.currentUser.user);
  const { members, configurators = [] } = useAppSelector((state: AppState) => state.currentUser.group || unknownGroup);
  const orderDealerOptions = useAppSelector((state: AppState) => state.currentUser.availableDealers || []);
  const dealersEnabled = useAppSelector(({ currentUser: { group = unknownGroup, availableDealers = [] } }: AppState) =>
    group.configurators
      ? group.configurators.some((c) =>
          getEnabledOnProperty(c.vendorData, ConfiguratorEnabledOnProps.DealerNetworkEnabled, true),
        ) && availableDealers.length > 0
      : false,
  );
  const runLeadsTour = useAppSelector((state) => state?.gettingStarted.runLeadsTour);
  const [runTour, setRunTour] = useState(false);

  const {
    leads: leadData,
    isLoadingLeads,
    orderOwnerOptions,
  } = useOrderLeadRepo({
    useLeads: true,
    useOrderOwner: true,
  });

  const stableOrderOwnerOptions = useMemo(() => orderOwnerOptions, [JSON.stringify(orderOwnerOptions)]);

  const getBuildingStyleOptions = useCallback(() => Array.from(new Set(leads.map((l) => l.buildingStyle))), [leads]);
  const getBuildingSizeOptions = useCallback(() => Array.from(new Set(leads.map((l) => l.buildingSize))), [leads]);
  const getOrderStatusOptions = useCallback(() => {
    const leadStatuses = Array.from(
      new Map(
        [...orderStatusOptions, ...leads.map((l) => getOrderStatusFromOrder(l))].map((status) => [status.id, status]),
      ).values(),
    );
    const defaultStatuses = leadStatuses.map((status) => ({
      value: status.id,
      label: i18n.t(status.i18nKey || '') || status.name,
    }));
    return defaultStatuses;
  }, [leads]);

  const isAdmin = isCurrentUserAdmin(user);
  const isManager = isCurrentUserManager(user);

  const columns = useMemo(
    () =>
      getColumnExtraProps(
        t,
        dispatch,
        members,
        configurators,
        stableOrderOwnerOptions,
        orderDealerOptions,
        getOrderStatusOptions,
        getBuildingStyleOptions,
        getBuildingSizeOptions,
        dealersEnabled,
        isAdmin,
        isManager,
      ),
    [
      t,
      dispatch,
      members,
      configurators,
      stableOrderOwnerOptions,
      orderDealerOptions,
      getOrderStatusOptions,
      getBuildingStyleOptions,
      getBuildingSizeOptions,
      dealersEnabled,
      isAdmin,
      isManager,
      leads, // Ensure the columns are updated when leads change
    ],
  );

  const rows = useMemo(() => leads.map((lead) => ({ ...lead })), [leads]); // Ensure a new reference for rows

  const scrollToJoyride = (): void => {
    let targetElement = document.querySelector(`.${I18nKeys.TableHeaderOwner}`);
    if (!targetElement) {
      targetElement = document.querySelector(`.${I18nKeys.TableHeaderSite}`);
    }
    if (targetElement && targetElement !== null) {
      setTimeout(() => {
        targetElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }, 300); // Give time for tooltip to be rendered
    }
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status } = data;
    if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED || status === STATUS.FINISHED) {
      if (status === STATUS.FINISHED) {
        dispatch(setRunLeadsTour(false));
      }
    }
  };

  const handleExport = useCallback(() => {
    apiRef?.current?.exportDataAsCsv();
  }, [apiRef]);

  useEffect(() => {
    if ((leads.length === 0 && leadData.length !== 0) || leads.length !== leadData.length) {
      dispatch(setLeads(leadData));
    }

    if (runLeadsTour && !isLoadingLeads && !runTour) {
      scrollToJoyride();
      setRunTour(true);
    }
  }, [dispatch, leadData, leads]);

  useLayoutEffect(() => {
    dispatch(setSearchHidden(false));

    if (isIdeaRoomUser() || isCurrentUserAdmin(user)) {
      dispatch(
        setSecondaryActions([
          <IconButton color="secondary" size="small" onClick={handleExport}>
            <FileDownloadOutlinedIcon />
          </IconButton>,
        ]),
      );
    }
  }, [dispatch, user, handleExport]);

  const onRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    (params: GridRowParams<any>) => {
      const { row: lead } = params;
      dispatch(openLeadDetailDialog({ leads, uuid: lead.uuid }));
    },
    [dispatch, leads],
  );

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <MuiDataGrid
        loading={isLoadingLeads}
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        enableDateFilter
        getRowId={(row) => row.hash}
        onRowClick={onRowClick}
        handleSearch
        autoHeight
        initialState={{
          columns: {
            orderedFields: columns.map((col) => col.field),
          },
        }}
        countParams={{
          countLabel: I18nKeys.LeadGridRowCount,
          filteredLabel: I18nKeys.LeadGridFilteredRowCount,
        }}
      />

      {!isLoadingLeads && runTour && (
        <JoyRide
          hideBackButton
          hideCloseButton
          run={runTour}
          steps={[
            {
              disableOverlay: true,
              placement: 'top' as Placement,
              spotlightClicks: true,
              target: `.${isAdmin || isManager ? I18nKeys.TableHeaderOwner : I18nKeys.TableHeaderStatus}`,
              content:
                'Update the Status and Owner as your sales team works the lead. Click on the row to see the building.',
              disableBeacon: true,
            },
          ]}
          tooltipComponent={JoyrideTooltip}
          scrollOffset={100}
          callback={handleJoyrideCallback}
          locale={{
            close: 'Got it',
          }}
          styles={{
            options: {
              zIndex: 1300,
            },
          }}
        />
      )}
    </Box>
  );
};
